// Progressively enhances the "Donation" option fieldset (rendered by the
// normal variant-picker machinery — see blocks/seal-donation-slider.liquid
// for why it's reused verbatim rather than hand-rolled) into a drag slider.
//
// Deliberately does NOT reimplement any variant-switching logic: dragging
// the slider just checks the matching radio input from the real fieldset and
// dispatches a native, bubbling 'change' event on it, exactly as if the
// shopper had clicked that pill directly — the ancestor <variant-picker>
// custom element (assets/variant-picker.js) picks that up itself and does
// everything else (price update, buy-button state, morphing, URL). If this
// script never runs, the plain pill buttons underneath still work.

class SealDonationSlider extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #observer;

  connectedCallback() {
    this.#init();
    this.#observer = new MutationObserver(() => this.#init());
    this.#observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #init() {
    const fieldset = this.querySelector('fieldset');
    const domRadios = fieldset ? Array.from(fieldset.querySelectorAll('input[type="radio"]')) : [];

    // The fieldset's own DOM order mirrors the product's Shopify variant
    // order, which was deliberately reordered (see productVariantsBulkReorder
    // in project memory) so +10% is FIRST — that's what makes it the
    // server-side default. But a slider has to read low-to-high left-to-right
    // regardless of which tier is the default, so ticks/track position are
    // built from a separately-sorted copy, parsed by the numeric % in each
    // radio's value (e.g. "+ 10%" -> 10). Falls back to DOM order for any
    // value that doesn't parse rather than risk misordering everything.
    const parsePercent = (value) => {
      const match = value.match(/(-?\d+(?:\.\d+)?)\s*%/);
      return match ? Number(match[1]) : NaN;
    };
    const radios = domRadios.every((radio) => !Number.isNaN(parsePercent(radio.value)))
      ? domRadios.slice().sort((a, b) => parsePercent(a.value) - parsePercent(b.value))
      : domRadios;

    // No Donation-shaped fieldset (product has no real option yet, or it's a
    // single unavailable value rendered as plain text) — nothing to enhance.
    // Any slider left over from a previous render (e.g. the fieldset just
    // got removed by a morph) should go with it.
    if (!fieldset || radios.length < 2) {
      this.querySelector('.seal-donation-slider__track')?.remove();
      return;
    }

    // Pause observing while WE mutate the DOM below, so our own inserts
    // don't immediately re-trigger this same callback.
    this.#observer?.disconnect();

    fieldset.classList.add('seal-donation-slider__fieldset');

    let track = this.querySelector('.seal-donation-slider__track');
    if (!track) {
      track = document.createElement('div');
      track.className = 'seal-donation-slider__track';
      track.innerHTML =
        '<input type="range" class="seal-donation-slider__range" min="0" step="1">' +
        '<div class="seal-donation-slider__ticks"></div>';
      fieldset.insertAdjacentElement('beforebegin', track);
    }

    const range = /** @type {HTMLInputElement} */ (track.querySelector('.seal-donation-slider__range'));
    const ticksEl = track.querySelector('.seal-donation-slider__ticks');

    range.max = String(radios.length - 1);

    // Rebuilt every time: the fieldset can be re-rendered with fresh radios
    // (different data-variant-id etc.) after a variant change, even though
    // the visible labels usually stay the same.
    //
    // Ticks are positioned at explicit i/(n-1) percentages, NOT laid out
    // with flex/space-between — a native range thumb's possible center
    // positions run from 0% to 100% of the track's PADDED width (it's
    // inset by half its own size at each end), while equal-width flex
    // slots center each label at (i + 0.5)/n instead. Those two schemes
    // don't line up, which is exactly the "dot doesn't match the
    // percentages" bug this replaces. The ticks container's padding-inline
    // (in the stylesheet) mirrors that same half-thumb inset so 0%/100%
    // here land on the exact same pixel as the thumb's end stops.
    if (ticksEl) {
      ticksEl.innerHTML = '';
      radios.forEach((radio, index) => {
        const tick = document.createElement('span');
        tick.className = 'seal-donation-slider__tick';
        if (radio.dataset.optionAvailable === 'false') {
          tick.classList.add('seal-donation-slider__tick--unavailable');
        }
        tick.style.left = `${(index / (radios.length - 1)) * 100}%`;
        tick.textContent = radio.value;
        ticksEl.appendChild(tick);
      });
    }

    let currentIndex = radios.findIndex((radio) => radio.checked);
    if (currentIndex === -1) {
      // No radio pre-checked (shouldn't normally happen — Shopify always
      // resolves a selected_or_first_available_variant server-side — but
      // if it does, prefer the +10% donation tier as the fallback default
      // rather than silently landing on whichever value happens to be
      // first in the option's configured order.
      currentIndex = radios.findIndex((radio) => radio.value.replace(/\s/g, '').includes('10%'));
    }
    if (currentIndex === -1) currentIndex = 0;
    range.value = String(currentIndex);
    this.#setActiveTick(track, currentIndex);

    if (!range.dataset.sealBound) {
      range.dataset.sealBound = 'true';
      range.addEventListener('input', () => {
        const index = Number(range.value);
        const radio = radios[index];
        this.#setActiveTick(track, index);
        if (!radio || radio.checked) return;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    this.#observer?.observe(this, { childList: true, subtree: true });
  }

  /**
   * @param {Element} track
   * @param {number} index
   */
  #setActiveTick(track, index) {
    track.querySelectorAll('.seal-donation-slider__tick').forEach((tick, tickIndex) => {
      tick.classList.toggle('seal-donation-slider__tick--active', tickIndex === index);
    });
  }
}

if (!customElements.get('seal-donation-slider')) {
  customElements.define('seal-donation-slider', SealDonationSlider);
}
